import type { ReactNode } from "react";

import classNames from "classnames";
import { useTranslation } from "react-i18next";
import ReactModal from "react-modal";

import { selectConfigItem } from "@/features/config/configSlice";

import { useAppSelector } from "@/app/hooks";

import "./modal.scss";

const customStyles = () => ({
  overlay: {
    background: "rgba(0,0,0, 0.60)",
    zIndex: 10,
  },
});

interface ModalProps {
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  onTapOutsideModal?: () => void;
  wrapperClassName?: string;
  contentLabel?: string;
  mobileFullscreen?: boolean;
  minHeightUnlimited?: boolean;
}

interface ModalSubcomponents {
  Title: typeof ModalTitle;
  SubTitle: typeof ModalSubTitle;
  Content: typeof ModalContent;
  List: typeof ModalList;
  ListElement: typeof ModalListElement;
  ButtonsArea: typeof ModalButtonsArea;
  Button: typeof ModalButton;
}

interface ModalTitle {
  children: ReactNode;
  className?: string;
  alignment?: "left" | "center";
  ariaHidden?: boolean;
}

interface ModalSubTitle {
  children: ReactNode;
  alignment?: "left" | "center";
}

interface ModalContent {
  children: ReactNode;
  alignment?: "left" | "center";
  ariaHidden?: boolean;
}

interface ModalListElement {
  id: string;
  isMultiline?: boolean;
  children: ReactNode;
}

interface ModalButton {
  children: ReactNode;
  onClick?: (e?: React.MouseEvent) => void;
  color?: "error";
  mode?: "text" | "contained";
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  form?: string;
}

const Modal = ({
  isOpen,
  children,
  className,
  onTapOutsideModal,
  wrapperClassName = "",
  mobileFullscreen = false,
  minHeightUnlimited = false,
  contentLabel = "",
}: ModalProps) => {
  const { t } = useTranslation();

  const isKioskMode = useAppSelector(
    selectConfigItem("feature_flags.kiosk_mode"),
  );
  return (
    <ReactModal
      contentLabel={contentLabel}
      style={customStyles()}
      isOpen={isOpen}
      appElement={document.getElementById("root") as HTMLElement}
      onRequestClose={onTapOutsideModal}
      aria={{
        labelledby: "modal-title",
      }}
      className={classNames(
        "modalWrapper",
        {
          "modalWrapper--fullscreen-mobile": mobileFullscreen,
          "modalWrapper--kiosk-mode": isKioskMode,
          "modalWrapper--unlimited": minHeightUnlimited,
        },
        wrapperClassName,
      )}
    >
      <div className={`${className ?? ""} modal`}>
        <span
          id="modal-title"
          className="sr-only"
          aria-label={t("accessibility.modal")}
          aria-hidden
        />
        {children}
      </div>
    </ReactModal>
  );
};

const ModalTitle = ({
  children,
  className,
  alignment,
  ariaHidden,
}: ModalTitle) => {
  return (
    <div
      className={`${className ?? ""} modal__title ${alignment === "center" ? "modal__title__centered" : ""}`}
      aria-hidden={ariaHidden}
    >
      {children}
    </div>
  );
};

const ModalSubTitle = ({ children, alignment }: ModalSubTitle) => {
  return (
    <div
      className={`modal__subtitle ${alignment === "center" ? "modal__subtitle__centered" : ""}`}
    >
      {children}
    </div>
  );
};

const ModalContent = ({
  children,
  alignment = "center",
  ariaHidden,
}: ModalContent) => {
  return (
    <div
      className={classNames("modal__content", {
        "left-aligned": alignment === "left",
      })}
      aria-hidden={ariaHidden}
    >
      {children}
    </div>
  );
};

const ModalList = ({ children }: { children: ReactNode }) => {
  return <ul className="modal__list">{children}</ul>;
};

const ModalListElement = ({
  id,
  isMultiline = false,
  children,
}: ModalListElement) => {
  return (
    <li
      key={id}
      className={`modal__list__element ${
        isMultiline ? "modal__list__element__multiline" : ""
      }`}
    >
      {children}
    </li>
  );
};

const ModalButtonsArea = ({
  children,
  mode = "vertical",
}: {
  children: ReactNode;
  mode?: "vertical" | "horizontal";
}) => {
  return (
    <div className={`modal__buttons modal__buttons--${mode}`}>{children}</div>
  );
};

const ModalButton = ({
  children,
  onClick,
  color,
  mode,
  disabled,
  type = "button",
  form,
}: ModalButton) => {
  const getClassNameForColor = (color?: "error") => {
    if (!color) return "";
    if (color === "error") return "modal__buttons__button__error";
    return "";
  };

  return (
    <button
      type={type}
      form={form}
      className={`modal__buttons__button ${getClassNameForColor(color)} ${mode ?? "text"}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export default Object.assign(Modal, {
  Button: ModalButton,
  ButtonsArea: ModalButtonsArea,
  Content: ModalContent,
  List: ModalList,
  ListElement: ModalListElement,
  SubTitle: ModalSubTitle,
  Title: ModalTitle,
}) as React.FC<ModalProps> & ModalSubcomponents;
